import { useState } from "react";
import { useNavigate } from "react-router";
import { usePetCharacter } from "@/features/home/PetFigure";
import { useActiveHome } from "@/shared/session/householdContext";
import { ErrorState, LoadingState, Page, TopBar } from "@/shared/ui";
import { BasketGame, FlightGame, SnakeGame } from "../ui";
import { PlayUnavailable } from "./PlayUnavailable";
import { GAMES, playNotice, playStatus, type GameId } from "./playStatus";
import "./play.css";

/**
 * 一款游戏的全屏页（/play/snake、/play/basket、/play/flight）。和选游戏页同一条规矩：只有 TA 在家、醒着才开局，
 * 直接打开链接也绕不过去；不能时写清原因、回选游戏页。规则只管「开局」：进来时能玩，这一趟就一直能玩，
 * 不会因为玩到一半 TA 睡着了（30 秒一次的复查）把正在玩的这局撤掉。
 * 不能时原因下面同选游戏页一样有「现在能做的事」（PlayUnavailable，R27 第 4 条），这里另给回小屋。
 * 谁在玩：当前这只（家园快照里的 pet）；局中不显示宠物切换栏（PET_BAR_ROUTES 的 none）。
 * 形象：星球形象（usePetCharacter），没有就用物种插画；动作清单还没有读口，一律 motion={null}，画静态。
 * 退出：游戏里的「回去」回选游戏页。
 */
export function PlayGamePage({ game }: { game: GameId }) {
  const navigate = useNavigate();
  const home = useActiveHome({ refetchInterval: 30_000 });
  const pet = home.data?.pet ?? null;
  const character = usePetCharacter(pet?.pet_id);
  const status = playStatus(home.data);
  const [entered, setEntered] = useState(false);
  if (status === "ok" && !entered) setEntered(true);
  const title = GAMES.find((item) => item.id === game)?.title ?? "小游戏";

  if (!entered) {
    const blocked = status === "ok" ? "unknown" : status;
    return (
      <Page bare className="ps-play">
        <TopBar title={title} back="/play" />
        {home.isPending ? (
          <LoadingState label="正在看 TA 在做什么…" lines={2} />
        ) : home.isError ? (
          <ErrorState error={home.error} onRetry={() => void home.refetch()} retrying={home.isFetching} />
        ) : (
          <>
            <div className="ps-play__notice" role="status">
              <p>{playNotice(blocked, pet?.name ?? "TA")}</p>
            </div>
            {home.data ? <PlayUnavailable status={blocked} snapshot={home.data} character={character} withHome /> : null}
          </>
        )}
      </Page>
    );
  }
  if (!pet) return null;
  const props = { pet: { pet_id: pet.pet_id, name: pet.name, species: pet.species }, character, onExit: () => navigate("/play") };
  return (
    <Page bare className="ps-play-game">
      {game === "snake" ? <SnakeGame {...props} motion={null} /> : game === "basket" ? <BasketGame {...props} motion={null} /> : <FlightGame {...props} />}
    </Page>
  );
}
